/**
 * Markdown处理相关的混入
 * 包含Markdown转HTML等方法
 */
import { marked } from "marked";
import DOMPurify from "dompurify";
// 引入JSON格式化工具
import JSONFormatter from "../JSONFormatter";

export default {
  methods: {
    escapeHtml(content) {
      return String(content == null ? "" : content)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },

    // 用户输入始终作为纯文本显示，避免聊天组件将标签当作 HTML 解析。
    formatUserMessageContent(content) {
      return `<span class="user-message-plain-text">${this.escapeHtml(
        content
      )}</span>`;
    },

    sanitizeMessageHtml(html) {
      return DOMPurify.sanitize(String(html == null ? "" : html), {
        USE_PROFILES: { html: true },
      });
    },

    // 将Markdown转换为HTML
    convertMarkdownToHtml(markdown) {
      try {
        // 配置marked选项，生成更干净的HTML
        const renderer = new marked.Renderer();
        const escapeHtml = this.escapeHtml;

        // 自定义代码块渲染器，使用deepseek风格
        renderer.code = function (code, language) {
          language = "";
          // 特殊处理JSON格式
          if (language === "json" && code) {
            try {
              // 使用JSONFormatter格式化JSON为单行
              return JSONFormatter.format(code);
            } catch (e) {
              console.warn("JSON格式化失败:", e);
            }
          }

          // 如果没有指定语言，不显示语言标识
          const languageHeader = language
            ? `<div class="code-language">${language}</div>`
            : "";

          return `
            <div class="code-block-container">
              <div class="code-block-header">
                ${languageHeader}
              </div>
              <pre class="code-block-content"><code class="language-${
                language || "text"
              }">${escapeHtml(code)}</code></pre>
            </div>
          `;
        };

        marked.setOptions({
          renderer: renderer,
          breaks: true, // 启用换行符转换为<br>
          gfm: true, // 启用GitHub风格的Markdown
          sanitize: false, // 允许HTML标签
          headerIds: false, // 禁用标题ID，避免生成额外的属性
          mangle: false, // 禁用转义，避免转义@和链接中的特殊字符
          smartLists: true, // 使用更智能的列表行为
          smartypants: false, // 禁用智能标点，避免额外的格式化
          xhtml: false, // 不使用xhtml规范的自闭合标签
          langPrefix: "language-", // 添加代码块的语言前缀
        });

        // 处理加载状态的情况
        if (markdown === "" && this.isStreaming) {
          this.isloading = true;
          return `<div class="markdown-body custom-markdown"><div class="loading-indicator">
            <span class="loading-dot"></span>
            <span class="loading-dot"></span>
            <span class="loading-dot"></span>
          </div></div>`;
          1;
        }
        this.isloading = false;
        // 预处理Markdown内容
        let processedMarkdown = markdown;

        // 处理think标签 - 即使是未完成的标签也进行处理
        // 先处理已经完成的think标签
        const completeThinkTagRegex = /<(think)>([\s\S]*?)<\/\1>/gs; // 只匹配完整的think标签
        // 流式处理未完成的think标签在下方逻辑中处理

        const thinkTags = [];
        let lastIndex = 0;
        let match;

        // 先处理完整的标签
        while (
          (match = completeThinkTagRegex.exec(processedMarkdown)) !== null
        ) {
          // console.log(`找到完整标签:`, {
          //   type: match[1],
          //   content: match[2].substring(0, 100) + "...",
          // });
          thinkTags.push({
            start: match.index,
            end: completeThinkTagRegex.lastIndex,
            content: match[2], // 使用match[2]获取标签内容
            type: match[1], // 使用match[1]获取标签类型
            complete: true,
          });
          lastIndex = completeThinkTagRegex.lastIndex;
        }

        // 然后处理未完成的标签（如果存在）——将最后一个未闭合的 <think> 视为流式内容
        const lastOpenIndex = processedMarkdown.lastIndexOf("<think>");
        const lastCloseIndex = processedMarkdown.lastIndexOf("</think>");
        if (
          lastOpenIndex !== -1 &&
          (lastCloseIndex === -1 || lastOpenIndex > lastCloseIndex)
        ) {
          const contentStart = lastOpenIndex + "<think>".length;
          const content = processedMarkdown.slice(contentStart);
          // console.log(`找到未完成标签:`, {
          //   type: "think",
          //   content: content.substring(0, 100) + "...",
          // });
          thinkTags.push({
            start: lastOpenIndex,
            end: processedMarkdown.length,
            content: content, // 截取到文本末尾的内容作为当前已生成的思考内容
            type: "think",
            complete: false,
          });
        }

        // console.log(`总共找到 ${thinkTags.length} 个标签`);

        // 构建占位符映射
        const placeholderMap = {};
        let modifiedMarkdown = "";
        let currentIndex = 0;

        // 逐步构建新的Markdown字符串
        for (let i = 0; i < thinkTags.length; i++) {
          const { start, end, content, type } = thinkTags[i];

          // 添加占位符之前的内容
          modifiedMarkdown += processedMarkdown.slice(currentIndex, start);

          // 创建唯一占位符（使用更简单的格式）
          const placeholder = `%%THINK_${i}%%`;
          placeholderMap[placeholder] = {
            content,
            type,
            complete: thinkTags[i].complete,
          };
          // console.log(`创建占位符 ${placeholder}:`, {
          //   type,
          //   contentLength: content?.length,
          //   complete: thinkTags[i].complete,
          // });

          // 添加占位符
          modifiedMarkdown += placeholder;

          currentIndex = end;
        }
        modifiedMarkdown += processedMarkdown.slice(currentIndex);
        processedMarkdown = modifiedMarkdown;
        // 特殊处理地址格式 (如TLaFV4UNN8D6g71CAFdXe6Q2u9xMuW1YFN)
        processedMarkdown = processedMarkdown.replace(
          /\b([A-Za-z0-9]{30,})\b/g,
          "$1"
        );

        // 确保代码块前后有空行，以便正确渲染
        processedMarkdown = processedMarkdown.replace(
          /```([\s\S]*?)```/g,
          "\n```$1```\n"
        );

        // 将转换后的HTML包裹在markdown-body和custom-markdown类中
        const html = marked(processedMarkdown);

        // 优化HTML
        let optimizedHtml = html
          // 移除列表项中的段落标签
          .replace(/<li>\s*<p>/g, "<li>")
          .replace(/<\/p>\s*<\/li>/g, "</li>")
          // 移除多余的空行
          .replace(/\n\s*\n/g, "\n")
          // 优化空白
          .replace(/\s{2,}/g, " ")
          // 优化换行符周围的空白
          .replace(/\s*<br>\s*/g, "<br>")
          // 确保列表项之间没有空白
          .replace(/<\/li>\s*<li>/g, "</li><li>")
          // 压缩列表开始和结束的间距
          .replace(/<ul>\s*/g, "<ul>")
          .replace(/\s*<\/ul>/g, "</ul>")
          .replace(/<ol>\s*/g, "<ol>")
          .replace(/\s*<\/ol>/g, "</ol>")
          // 移除列表前后的空白
          .replace(/([^>])\s*<ul>/g, "$1<ul>")
          .replace(/<\/ul>\s*([^<])/g, "</ul>$1")
          .replace(/([^>])\s*<ol>/g, "$1<ol>")
          .replace(/<\/ol>\s*([^<])/g, "</ol>$1")
          // 特殊处理列表开始和结束处的空白行问题
          .replace(/<\/p>\s*<ul>/g, "</p><ul>")
          .replace(/<\/ul>\s*<p>/g, "</ul><p>")
          .replace(/<\/p>\s*<ol>/g, "</p><ol>")
          .replace(/<\/ol>\s*<p>/g, "</ol><p>")
          // 将多个换行符压缩成一个
          .replace(/<br>\s*<br>/g, "<br>");

        // 将表格包装在滚动容器中
        optimizedHtml = optimizedHtml.replace(
          /<table>/g,
          '<div class="table-container"><table>'
        );
        optimizedHtml = optimizedHtml.replace(/<\/table>/g, "</table></div>");

        // 处理特殊格式，如：key: value 的形式
        optimizedHtml = optimizedHtml.replace(
          /([A-Za-z0-9_]+):\s+(.*?)(?=<br>|<\/p>|<\/li>|$)/g,
          '<span class="key-value"><span class="key">$1:</span><span class="value">$2</span></span>'
        );

        // 增强列表标记，直接在HTML元素上添加样式
        optimizedHtml = optimizedHtml
          // 为无序列表添加样式 - 移除列表原生标记
          .replace(
            /<ul>/g,
            '<ul style="list-style-type: none !important; padding-left: 0.5em !important;">'
          )
          // 为有序列表添加样式 - 移除列表原生标记
          .replace(
            /<ol>/g,
            '<ol style="list-style-type: none !important; padding-left: 0.5em !important;">'
          )
          // 为列表项添加样式，并添加醒目的标记
          .replace(
            /<li>/g,
            '<li style="padding-left: 20px !important; position: relative; display: block !important;">'
          );

        // 使用单独的处理逻辑，确保不会重复添加标记
        // 为无序列表项添加醒目的蓝色圆点标记
        optimizedHtml = optimizedHtml.replace(
          /<ul style="[^"]*?">([\s\S]*?)<\/ul>/g,
          function (match) {
            // 只处理顶层的li元素，不处理已有标记的元素
            return match.replace(
              /<li style="([^"]*?)">(?!<span style="position: absolute)/g,
              '<li style="$1"><span style="position: absolute; left: 0; color: #4285f4; font-weight: bold;">•&nbsp;</span>'
            );
          }
        );

        // 为有序列表项添加醒目的蓝色方块标记
        optimizedHtml = optimizedHtml.replace(
          /<ol style="[^"]*?">([\s\S]*?)<\/ol>/g,
          function (match) {
            // 只处理顶层的li元素，不处理已有标记的元素
            return match.replace(
              /<li style="([^"]*?)">(?!<span style="position: absolute)/g,
              '<li style="$1"><span style="position: absolute; left: 0;top: -0.1em; color: #4285f4; font-weight: bold;">▪&nbsp;</span>'
            );
          }
        );

        // 还原<think>标签内容，并添加可折叠的HTML结构
        // 替换占位符（使用字符串替换而非正则）
        for (let i = 0; i < thinkTags.length; i++) {
          const placeholder = `%%THINK_${i}%%`;

          if (!optimizedHtml.includes(placeholder)) {
            console.warn(`占位符未找到: ${placeholder}`);
            continue;
          }

          try {
            // 处理标签内容
            const { content, type, complete } = placeholderMap[placeholder];
            // console.log(`处理标签 ${i}:`, {
            //   type,
            //   contentLength: content?.length,
            //   complete,
            // });

            let processedContent = content;
            let title = "思考过程";

            // 预处理思考内容中的列表，确保列表项正确渲染
            let processedThinkContent = processedContent
              // 确保列表项前有空行
              .replace(/\n([*\-+])\s/g, "\n\n$1 ")
              .replace(/\n(\d+\.)\s/g, "\n\n$1 ")
              // 处理嵌套列表的缩进
              .replace(/\n\s{2,}([*\-+])\s/g, "\n  $1 ")
              .replace(/\n\s{2,}(\d+\.)\s/g, "\n  $1 ");

            // 确保思考内容作为完整的独立内容处理，避免被上下文干扰
            let finalContent = marked(processedThinkContent);

            // 如果是未完成的标签，添加加载指示器
            // if (!complete) {
            //   finalContent += `<div class="loading-indicator think-loading">
            //     <span class="loading-dot"></span>
            //     <span class="loading-dot"></span>
            //     <span class="loading-dot"></span>
            //   </div>`;
            // }

            // 创建可折叠的组件，确保内容正确显示
            const collapsibleHTML = `
              <div class="think-container">
                <details class="think-details" open>
                  <summary class="think-summary">${title}</summary>
                  <div class="think-content">${finalContent}</div>
                </details>
              </div>
            `;

            optimizedHtml = optimizedHtml
              .split(placeholder)
              .join(collapsibleHTML);
          } catch (error) {
            console.error(`处理标签错误:`, error);
            console.error(`错误详情:`, {
              placeholder,
              placeholderMap: placeholderMap[placeholder],
              thinkTag: thinkTags[i],
            });
            const tagType = thinkTags[i]?.type || "unknown";
            optimizedHtml = optimizedHtml.replace(
              placeholder,
              `<div class="${tagType}-content-error">思考内容处理错误: ${error.message}</div>`
            );
          }
        }

        return this.sanitizeMessageHtml(
          `<div class="markdown-body custom-markdown">${optimizedHtml}</div>`
        );
      } catch (error) {
        console.error("Markdown转HTML出错:", error);
        // 返回带有错误提示的HTML
        return this.sanitizeMessageHtml(`<div class="markdown-error">
      <p>内容渲染错误</p>
      <details>
        <summary>错误详情</summary>
        <pre>${error.message}</pre>
      </details>
    </div>`);
      }
    },

    // 过滤系统消息
    filterSystemMessages(text) {
      const systemMessages = [
        "AI处理完成",
        "AI处理中",
        "AI回复完成",
        "AI已完成回复",
        "AI已处理完成",
        "[DONE]",
      ];

      return systemMessages.reduce(
        (result, msg) =>
          result
            .replace(new RegExp(msg + "。?$", "g"), "")
            .replace(new RegExp(msg + "$", "g"), ""),
        text
      );
    },
  },
  mounted() {
    // 添加全局JSON折叠函数
    if (typeof window !== "undefined" && !window.toggleJsonCollapse) {
      window.toggleJsonCollapse = function (id) {
        const element = document.getElementById(id);
        if (element) {
          const parent = element.parentNode;
          parent.classList.toggle("json-formatter-collapsed");
        }
      };
    }
  },
};
